import { apiAxiosInstance } from '@/lib/api/config';
import type { InternalAxiosRequestConfig } from 'axios';
import { createLogger } from '@/utils/logger';

const log = createLogger('ClientContextInterceptor');

/**
 * Axios interceptor to inject X-Active-Client-Id header
 * This header is required by backend middleware for client context management
 *
 * Header Requirements:
 * - Client role users: REQUIRED on all API requests
 * - Preparer/Admin users: OPTIONAL (can be omitted for multi-client views)
 *
 * The selected client ID is read from sessionStorage where it's managed by ClientContext
 */
export function setupClientContextInterceptor(): void {
  apiAxiosInstance.interceptors.request.use(
    (config: InternalAxiosRequestConfig) => {
      // Skip adding header for auth endpoints and client listing endpoint
      if (
        config.url?.includes('/api/Auth/') ||
        config.url?.includes('/api/Clients/my-clients') ||
        config.url?.includes('/api/Clients/available')
      ) {
        return config;
      }

      // Get selected client from sessionStorage
      const selectedClientJson = sessionStorage.getItem('selectedClient');

      if (selectedClientJson) {
        try {
          const selectedClient = JSON.parse(selectedClientJson);
          if (selectedClient?.id) {
            // Inject X-Active-Client-Id header
            if (config.headers) {
              config.headers['X-Active-Client-Id'] = selectedClient.id;
            }
          }
        } catch (err) {
          // Log the error for debugging, but don't throw - let request proceed without header
          log.debug('Failed to parse selectedClient from sessionStorage:', err);
        }
      }

      return config;
    },
    (error) => Promise.reject(error)
  );
}
